import { useEffect, useMemo } from 'react';

import { useKeyboardShortcutsContext } from '../context-providers/KeyboardShortcutsProvider';
import { KeyboardShortcut, ShortcutHandler } from '../utils/keyboardShortcuts';

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  id: string;
  priority?: number;
  enabled?: boolean;
}

/**
 * Hook to register keyboard shortcuts
 *
 * @example
 * useKeyboardShortcuts({
 *   id: 'navigation',
 *   shortcuts: [
 *     {
 *       key: '1',
 *       description: 'Go to Calls',
 *       category: 'navigation',
 *       action: () => navigate('/calls'),
 *     },
 *   ],
 * });
 */
export const useKeyboardShortcuts = ({
  shortcuts,
  id,
  priority = 0,
  enabled = true,
}: UseKeyboardShortcutsOptions) => {
  const { registerShortcuts, unregisterShortcuts } = useKeyboardShortcutsContext();

  const handler: ShortcutHandler = useMemo(
    () => ({
      id,
      shortcuts: enabled ? shortcuts : [],
      priority,
    }),
    [id, shortcuts, priority, enabled],
  );

  useEffect(() => {
    if (enabled) {
      registerShortcuts(handler);
    }

    return () => {
      unregisterShortcuts(id);
    };
  }, [handler, enabled, registerShortcuts, unregisterShortcuts, id]);
};
